import { getSiteConfig, getSiteMetadata } from "@base/config";
import {
  CaptureUpsellPaymentV2Input,
  CCBillAuthTokenResponse,
  CreateSubscriptionV2Input,
  CreateSubscriptionV3Input,
  AttractedTo,
  CreateUserInput,
  UpsertWebSessionInput,
} from "./types";

import {
  createTreatyClient,
  projectNameHeaders,
} from "@neontree/bible-api-client";
import { createAuthClient } from "better-auth/react";
import { magicLinkClient } from "better-auth/client/plugins";
import * as Sentry from "@sentry/gatsby";
import {
  checkoutHeaders,
  clearCheckoutToken,
  getCheckoutToken,
} from "@base/lib/checkoutSession";

export const coreapi = {
  startCheckout,
  sortCheckoutEmail,
  jumpCheckout,
  redeemCheckout,
  getCheckoutMe,
  createSubscription,
  createSubscriptionV2,
  captureUpsellPaymentV2,
  getUserGeolocationInfo,
  sendMagicLink,
  getLatestSessionByUserID,
  getOrderDetails,
  postponeNextBill,
  getSession,
  getCCBillAuthToken,
  capturePaymentV3,
};

const REQUEST_TIMEOUT_MS = 15_000;

function fetchWithTimeout(
  input: RequestInfo | URL,
  init?: RequestInit,
): Promise<Response> {
  // Respect an existing signal (don't override caller aborts) and skip on
  // browsers without AbortController rather than breaking the request.
  if (init?.signal || typeof AbortController === "undefined") {
    return fetch(input, init);
  }

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
  return fetch(input, { ...init, signal: controller.signal }).finally(() =>
    clearTimeout(timer),
  );
}

// Timeout on every request so money-path calls can't hang a spinner forever,
// one retry for GETs only (retrying POSTs could double-submit payments), and
// failures reported instead of surfacing as an opaque stuck UI.
// console.error is also shipped to PostHog via capture_console_errors.
async function reliableFetch(
  input: RequestInfo | URL,
  init?: RequestInit,
): Promise<Response> {
  const method = (init?.method ?? "GET").toUpperCase();

  try {
    return await fetchWithTimeout(input, init);
  } catch (firstError) {
    if (method === "GET") {
      try {
        return await fetchWithTimeout(input, init);
      } catch (retryError) {
        reportFetchFailure(retryError, input, method, { retried: true });
        throw retryError;
      }
    }

    reportFetchFailure(firstError, input, method, { retried: false });
    throw firstError;
  }
}

function reportFetchFailure(
  error: unknown,
  input: RequestInfo | URL,
  method: string,
  extra: Record<string, unknown>,
) {
  const url =
    typeof input === "string"
      ? input
      : input instanceof URL
        ? input.href
        : input.url;
  console.error("coreapi request failed", { url, method, ...extra, error });
  Sentry.captureException(error, { extra: { url, method, ...extra } });
}

// A dead checkout session answers 401. Drop it, so the next email step starts a new one.
function dropDeadCheckoutToken<R extends { error: { status: unknown } | null }>(res: R): R {
  if (res.error?.status === 401 && getCheckoutToken()) clearCheckoutToken();
  return res;
}

// Clients are created lazily so the app's config registry is populated first;
// memoized so every call shares one instance.
let _coreapiClient: ReturnType<typeof createTreatyClient> | undefined;
function coreapiClient() {
  if (!_coreapiClient) {
    _coreapiClient = createTreatyClient(getSiteConfig().coreApiHost, {
      fetch: {
        credentials: "include",
      },
      projectName: getSiteMetadata().projectName,
      fetcher: reliableFetch,
    });
  }
  return _coreapiClient;
}

let _authClient: ReturnType<typeof createAuthClientInstance> | undefined;
function createAuthClientInstance() {
  return createAuthClient({
    baseURL: getSiteConfig().coreApiHost,
    basePath: "/auth",
    plugins: [magicLinkClient()],
    fetchOptions: {
      credentials: "include",
      // Must live inside fetchOptions — better-auth ignores top-level headers.
      headers: projectNameHeaders(getSiteMetadata().projectName),
      timeout: REQUEST_TIMEOUT_MS,
    },
  });
}
function authClient() {
  if (!_authClient) {
    _authClient = createAuthClientInstance();
  }
  return _authClient;
}

async function sendMagicLink(email: string): Promise<void> {
  const webappHost = getSiteConfig().webappHost;
  const { error } = await authClient().signIn.magicLink({
    email,
    callbackURL: webappHost,
    newUserCallbackURL: webappHost, //FIXME: add proper link
    errorCallbackURL: webappHost, //FIXME: add proper link
  });

  if (error) {
    throw new Error(error.message);
  }
}

async function getSession() {
  const { data, error } = await authClient().getSession();

  if (error) {
    return null;
  }

  return data;
}

/**
 * The quiz's "partner-type" answer is the user's `attracted_to` (a real column
 * on the core user with its own enum). Only the enum values are sent; any
 * other legacy answer value is dropped rather than stored as JSON.
 */
const ATTRACTED_TO_VALUES: readonly AttractedTo[] = ["male", "female", "other"];
function toAttractedTo(value: unknown): AttractedTo | undefined {
  return ATTRACTED_TO_VALUES.find((v) => v === value);
}

function toUserProfileBody(input: CreateUserInput) {
  const {
    email,
    name,
    gender,
    age,
    birth_place_lat,
    birth_place_lng,
    dob_utc,
    "partner-type": partnerType,
    ...rest
  } = input;

  return {
    gender,
    attracted_to: toAttractedTo(partnerType),
    age,
    email,
    name,
    current_tz_id: Intl.DateTimeFormat().resolvedOptions().timeZone,
    custom_properties: rest,
    birth_place_lat,
    birth_place_lng,
    dob_utc,
  };
}

// Cookies go along so the sid cookie links this web session to the new user.
async function startCheckout(input: CreateUserInput) {
  return coreapiClient().checkout.start.post(toUserProfileBody(input), {
    fetch: { credentials: "include" },
  });
}

async function sortCheckoutEmail(email: string) {
  return coreapiClient()
    .checkout.sort.post({ email }, { headers: checkoutHeaders() })
    .then(dropDeadCheckoutToken);
}

async function jumpCheckout() {
  return coreapiClient()
    .checkout.jump.post(undefined, { headers: checkoutHeaders() })
    .then(dropDeadCheckoutToken);
}

async function redeemCheckout(jumpToken: string) {
  return coreapiClient().checkout.redeem.post({ jumpToken });
}

// Not /users/me: it answers 401 for a checkout with no user, and that 401 would
// drop a live token. No cookies: the checkout session alone decides (C4).
async function getCheckoutMe() {
  return coreapiClient()
    .checkout.me.get({
      headers: checkoutHeaders(),
      fetch: { credentials: "omit" },
    })
    .then(dropDeadCheckoutToken);
}

async function createSubscription({
  userID,
  priceID,
  couponIDs,
  oneTimeFeePriceID,
}: {
  userID: string;
  priceID: string;
  couponIDs?: string[];
  oneTimeFeePriceID?: string;
}) {
  return coreapiClient().payments.createSubscription.post({
    userID,
    priceID,
    oneTimeFeePriceID,
    couponIDs,
  });
}

async function createSubscriptionV2(input: CreateSubscriptionV2Input) {
  return coreapiClient()
    .payments.v2.createSubscription.post(input, {
      headers: checkoutHeaders(),
      fetch: { credentials: "include" },
    })
    .then(dropDeadCheckoutToken);
}

async function captureUpsellPaymentV2(input: CaptureUpsellPaymentV2Input) {
  return coreapiClient()
    .payments.v2.captureUpsellPayment.post(input, { headers: checkoutHeaders() })
    .then(dropDeadCheckoutToken);
}

async function getUserGeolocationInfo() {
  return coreapiClient().geolocation.info.get();
}

/**
 * Raw `POST /session` transport. Do not call directly — go through
 * `services/webSession.upsertWebSession`, which owns sid decoration and
 * persistence of the confirmed session.
 */
export async function postWebSession(body: UpsertWebSessionInput) {
  return coreapiClient().session.post(body, {
    fetch: {
      credentials: "include",
    },
  });
}

async function getLatestSessionByUserID(userID: string) {
  return coreapiClient().getLatestFunnelSessionByUserId.get({
    query: { userId: userID },
  });
}

async function getOrderDetails(orderID: string) {
  return coreapiClient().payments.v2.orders.details.get({ query: { orderID } });
}

/**
 * Push the authed user's next bill out by `days` (1-30) — "extend trial".
 * Server allows this once per subscription and only during the first billing
 * cycle; check `trialExtendedAt` on the subscription summary before offering it.
 */
async function getCCBillAuthToken(): Promise<CCBillAuthTokenResponse> {
  const res = await coreapiClient().payments.v3["auth-token"].get();

  if (res.error) {
    throw new Error(`CCBill auth token request failed: ${res.error.status}`);
  }

  return res.data as CCBillAuthTokenResponse;
}

async function capturePaymentV3(input: CreateSubscriptionV3Input) {
  return coreapiClient()
    .payments.v3.capturePayment.post(input, {
      headers: checkoutHeaders(),
      fetch: { credentials: "include" },
    })
    .then(dropDeadCheckoutToken);
}

async function postponeNextBill(days: number) {
  return coreapiClient().subscriptions.postponeNextBill.post(
    { days },
    {
      fetch: {
        credentials: "include",
      },
    },
  );
}
